import React from "react";
import styled from "styled-components";
import { ButtonComp, CardComp, FormikSelectComp } from "../../../../components";
import { Form, Formik, useFormikContext } from "formik";
import { TextInputComp, FormikDatePicker } from "../../../../components";
import TextEditor from "../../modules/TextEditor";
import { useGetCommunicationBalancesQuery } from "../../../../store/admin/communicationSlice";
import { useGetEminuteEstimateSmsQuery } from "../../../../store/admin/eminutesSlice";
import { formatMoney } from "../../../../utils";

const AUDIENCE_OPTIONS = [
  { value: "MEMBER", label: "Members only" },
  { value: "STAFF", label: "Staff only" },
  { value: "BOTH", label: "Both members & staff" },
];

function AudienceSection() {
  return (
    <CardComp cardClassName={"pt-4 mt-4"} cardBodyClassName={"py-0"}>
      <FormikSelectComp
        fieldName="audience"
        labelText="Who should see this?"
        labelClassName="fw-bold"
        options={AUDIENCE_OPTIONS}
        selectText="Select audience"
        isRequired
        isSearchable={false}
      />
    </CardComp>
  );
}

function NotifySection({ showPublishOptions }) {
  const { values, setFieldValue } = useFormikContext();
  const audience = values.audience ?? "BOTH";
  const sendSms = values.send_sms ?? false;

  const { data: balancesData } = useGetCommunicationBalancesQuery();
  const smsBalanceKobo =
    balancesData?.smsBalanceKobo ?? balancesData?.smsBalance ?? 0;
  const smsBalanceLabel =
    balancesData?.smsBalanceNaira != null
      ? formatMoney(balancesData.smsBalanceNaira, true)
      : formatMoney(smsBalanceKobo / 100, true);

  const { data: estimateData } = useGetEminuteEstimateSmsQuery(audience, {
    skip: !sendSms || !audience || !showPublishOptions,
  });
  const estimatedRecipients = estimateData?.count ?? 0;
  const estimateChargeKobo = estimateData?.charge_kobo ?? 0;
  const estimateChargeLabel =
    estimateData?.charge_naira != null
      ? formatMoney(estimateData.charge_naira, true)
      : formatMoney(estimateChargeKobo / 100, true);
  const insufficientBalance =
    sendSms &&
    estimateChargeKobo > 0 &&
    estimateChargeKobo > smsBalanceKobo;

  if (!showPublishOptions) {
    return null;
  }

  return (
    <CardComp cardClassName={"pt-4 mt-4"} cardBodyClassName={"py-0"}>
      <h5 className="fw-bold text-black2 mb-3">Notify recipients via:</h5>
      <div className="d-flex flex-column gap-2">
        <div className="d-flex align-items-center justify-content-between border-bottom pb-2">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="channel_email"
              checked
              disabled
            />
            <label className="form-check-label fw-bold text-muted" htmlFor="channel_email">
              Email
            </label>
          </div>
          <span
            className="px-3 py-1 rounded-pill"
            style={{ background: "#dafceb", color: "#19bc31", fontSize: "12px", fontWeight: "600" }}
          >
            Always on
          </span>
        </div>
        <div className="d-flex align-items-center justify-content-between border-bottom pb-2">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="channel_push"
              checked
              disabled
            />
            <label className="form-check-label fw-bold text-muted" htmlFor="channel_push">
              Push Notification
            </label>
          </div>
          <span
            className="px-3 py-1 rounded-pill"
            style={{ background: "#dafceb", color: "#19bc31", fontSize: "12px", fontWeight: "600" }}
          >
            Always on
          </span>
        </div>
        <div className="d-flex align-items-center justify-content-between">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="channel_sms"
              checked={!!sendSms}
              onChange={(e) => setFieldValue("send_sms", e.target.checked)}
            />
            <label className="form-check-label pointer fw-bold" htmlFor="channel_sms">
              SMS
            </label>
          </div>
          <span
            className="px-3 py-1 rounded-pill"
            style={{ background: "#ffe5e5", color: "#ff4343", fontSize: "12px", fontWeight: "600" }}
          >
            Paid
          </span>
        </div>
      </div>
      {sendSms && (
        <div className="mt-3 p-3 rounded" style={{ background: "#f7f7f7", border: "1px solid #e0e0e0" }}>
          <p className="mb-1 fw-bold small">Your SMS balance: {smsBalanceLabel}</p>
          <p className="mb-1 small">
            Estimated charge (this send): {estimateChargeLabel}
            {estimatedRecipients > 0 && (
              <span className="text-blacklight">
                {" "}
                — {estimatedRecipients} recipient
                {estimatedRecipients === 1 ? "" : "s"}
                {estimateData?.pages != null &&
                  `, ${estimateData.pages} SMS page${estimateData.pages === 1 ? "" : "s"} each`}
              </span>
            )}
          </p>
          <p className="mb-0 small text-blacklight">
            SMS is billed in Naira from your organisation&apos;s SMS wallet (per page × recipients).
          </p>
          {insufficientBalance && (
            <p className="mt-2 mb-0 small fw-bold text-danger">
              Insufficient balance. Publishing with SMS will fail unless you top up or disable SMS.
            </p>
          )}
        </div>
      )}
    </CardComp>
  );
}

const EMinuteEditor = ({
  initialValues,
  handleContentChange,
  handleSaveDraft,
  handleSaveAndPublish,
}) => {
  const [submitMode, setSubmitMode] = React.useState(null);
  const [showPublishOptions, setShowPublishOptions] = React.useState(false);

  const handleSubmit = async (values, formikHelpers) => {
    try {
      if (submitMode === "publish") {
        await handleSaveAndPublish(values, formikHelpers);
      } else {
        await handleSaveDraft(values, formikHelpers);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      formikHelpers.setSubmitting(false);
      setSubmitMode(null);
    }
  };

  return (
    <Wrapper>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ submitForm, isSubmitting }) => (
          <Form>
            <CardComp
              cardClassName={"pt-4"}
              cardBodyClassName={"py-0 row justify-content-between"}
            >
              <div className="col-12 col-lg-5">
                <TextInputComp
                  name="title"
                  labelText={"E-Minutes Title"}
                  LabelClassName={"fw-bold"}
                />
              </div>
              <div className="col-12 col-lg-5">
                <FormikDatePicker
                  fieldName={"date"}
                  labelClassName={"fw-bold"}
                  labelText={"Minutes date"}
                />
              </div>
            </CardComp>
            <AudienceSection />
            <TextEditor
              initialValue={initialValues?.content}
              handleContentChange={handleContentChange}
            />
            <NotifySection showPublishOptions={showPublishOptions} />
            <div className="d-flex gap-4 mt-4">
              <ButtonComp
                containerClassName={"w-auto"}
                btnText={isSubmitting ? "Saving..." : "Save"}
                btnClassName={"py-2 px-4 rounded border-2"}
                type="button"
                onClick={() => {
                  setSubmitMode("draft");
                  setShowPublishOptions(false);
                  submitForm();
                }}
                disabled={isSubmitting}
              />
              <ButtonComp
                containerClassName={"w-auto"}
                btnText={isSubmitting ? "Saving..." : "Save & Publish"}
                btnClassName={"bg-primary1 py-2 px-4 text-white border-0 rounded"}
                type="button"
                onClick={() => {
                  setShowPublishOptions(true);
                  setSubmitMode("publish");
                  submitForm();
                }}
                disabled={isSubmitting}
                onMouseEnter={() => setShowPublishOptions(true)}
                onFocus={() => setShowPublishOptions(true)}
              />
            </div>
          </Form>
        )}
      </Formik>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  input {
    background: #fafafa;
    border: 1px solid #a1a0a0;
    border-radius: 5px;
  }
  button {
    min-width: 200px;
    border-radius: 10px;
    border: 1px solid var(--color-primary);
  }
  button.save {
    color: var(--color-primary);
  }
`;

export default EMinuteEditor;
